import { Button } from '@lms/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@lms/ui/dialog';
import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import { useToast } from '@lms/ui/use-toast';
import { LoaderCircle } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslations } from 'use-intl';
import { addUser } from '@/features/users/users-api';

// Adds someone to the customer (a portfolio row, no location access). They
// then show up as an empty column in the matrix, ready to be given access.
export function AddUserDialog({
  portfolioId,
  open,
  onOpenChange,
  onAdded,
}: {
  portfolioId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: () => Promise<unknown>;
}) {
  const t = useTranslations();
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const title = t('admin.add', { what: t('admin.user').toLowerCase() });

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPending(true);
    setError(undefined);
    try {
      await addUser({
        name,
        email,
        siteId: portfolioId,
        canManageGroups: false,
        canManageUsers: false,
      });
      await onAdded();
      toast({ title: t('admin.added', { what: name }) });
      setName('');
      setEmail('');
      onOpenChange(false);
    } catch (err) {
      setError(`${err}`);
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {t('access_matrix.add_user_help')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="add-user-name">{t('admin.name')}</Label>
            <Input
              id="add-user-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="add-user-email">{t('admin.email')}</Label>
            <Input
              id="add-user-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('access_matrix.cancel')}
            </Button>
            <Button type="submit" disabled={pending || !name || !email}>
              {pending && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {title}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
